import { useState, useEffect } from 'react';
import { Mail, UserX, Ban, UserPlus, Share2 } from 'lucide-react';
import { MemberSharingDialog } from './MemberSharingDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-keys';
import { captureError } from '@/lib/sentry';
import { format } from 'date-fns';

interface Member {
  user_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: 'owner' | 'admin' | 'member';
  status: 'active' | 'pending' | 'suspended';
  avatar_url?: string;
  is_owner: boolean;
  is_pending: boolean;
}

interface PendingInvite {
  id: string;
  invitee_email: string;
  member_role: 'admin' | 'member';
  created_at: string;
  updated_at: string;
}

export function MemberSettings() {
  const [members, setMembers] = useState<Member[]>([]);
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member');
  const [isInviting, setIsInviting] = useState(false);
  const [sharingDialogOpen, setSharingDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferTargetUserId, setTransferTargetUserId] = useState<string | null>(null);
  const [isTransferring, setIsTransferring] = useState(false);
  // useWorkspaces() (from WorkspaceContext) exposes a `refetch` method that
  // re-fetches the workspaces list and re-derives currentWorkspace.role.
  const { currentWorkspace, refetch: refetchWorkspaces } = useWorkspaces();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Viewer's role MUST come from currentWorkspace.role (the canonical source
  // populated by WorkspaceContext on login / workspace switch). This is the
  // same source WorkspaceSettings.tsx uses, so all role-gated UI in the app
  // stays consistent. Deriving from members[] would break for users who can
  // see the page but aren't yet present in members[] (loading race) and would
  // diverge from WorkspaceSettings's gating.
  const viewerRole = (currentWorkspace?.role ?? null) as
    | 'owner'
    | 'admin'
    | 'member'
    | null;
  const viewerIsOwner = viewerRole === 'owner';

  const isSingleMember = members.length === 1;

  useEffect(() => {
    if (currentWorkspace) {
      Promise.all([
        fetchMembers(),
        fetchPendingInvites()
      ]).finally(() => setLoading(false));
    }
  }, [currentWorkspace?.id]);

  const fetchPendingInvites = async () => {
    if (!currentWorkspace) return;

    try {
      const { data, error } = await supabase
        .rpc('get_pending_invitations', { p_workspace_id: currentWorkspace.id });

      if (error) throw error;
      setPendingInvites(data || []);
    } catch (error) {
      console.error('Error fetching pending invites:', error);
      captureError(error as Error, { feature: 'pending-invites-fetch' });
      toast({
        title: "Error",
        description: "Failed to load pending invitations",
        variant: "destructive",
      });
    }
  };

  const fetchMembers = async () => {
    if (!currentWorkspace) return;

    try {
      const { data, error } = await supabase
        .rpc('get_workspace_members', {
          p_workspace_id: currentWorkspace.id
        });

      if (error) throw error;
      setMembers(data || []);
    } catch (error) {
      console.error('Error fetching members:', error);
      captureError(error as Error, { feature: 'workspace-members-fetch' });
      toast({
        title: "Error",
        description: "Failed to load members",
        variant: "destructive",
      });
    }
  };

  const updateMemberRole = async (userId: string, newRole: string) => {
    try {
      // After this migration "admin" is no longer the unique managerial tier;
      // count the combined owner+admin pool so demoting the only admin while
      // an owner exists is allowed.
      const managerialCount = members.filter(
        m => (m.role === 'owner' || m.role === 'admin') && m.status === 'active'
      ).length;
      const member = members.find(m => m.user_id === userId);

      // Prevent changing role if this would leave workspace without owner/admin
      if ((member?.role === 'owner' || member?.role === 'admin') && managerialCount <= 1) {
        toast({
          title: "Error",
          description: "Workspace must have at least one owner or admin",
          variant: "destructive",
        });
        return;
      }

      // Owner role cannot be assigned via dropdown — only via transfer flow.
      if (newRole === 'owner') {
        toast({
          title: "Use 'Transfer Ownership'",
          description: "Promoting to owner is done through the transfer-ownership flow.",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase.rpc('update_workspace_member_role', {
        p_workspace_id: currentWorkspace?.id,
        p_user_id: userId,
        p_new_role: newRole,
      });

      if (error) throw error;

      toast({
        description: "Member role updated successfully",
      });
      
      fetchMembers();
    } catch (error) {
      captureError(error as Error, { feature: 'member-role-update' });
      toast({
        title: "Error",
        description: "Failed to update member role",
        variant: "destructive",
      });
    }
  };

  const updateMemberStatus = async (userId: string, newStatus: 'active' | 'suspended') => {
    try {
      const { error } = await supabase
        .from('workspace_members')
        .update({ status: newStatus })
        .eq('user_id', userId)
        .eq('workspace_id', currentWorkspace?.id);

      if (error) throw error;

      toast({
        description: `Member ${newStatus === 'active' ? 'activated' : 'suspended'} successfully`,
      });
      
      fetchMembers();
    } catch (error) {
      captureError(error as Error, { feature: 'member-status-update' });
      toast({
        title: "Error",
        description: "Failed to update member status",
        variant: "destructive",
      });
    }
  };

  const removeMember = async (userId: string) => {
    if (!currentWorkspace?.id) return;

    try {
      const { error } = await supabase.rpc('remove_workspace_member', {
        p_workspace_id: currentWorkspace.id,
        p_user_id: userId,
      });

      if (error) throw error;

      toast({
        description: "Member removed successfully",
      });

      await fetchMembers();
    } catch (error: any) {
      const message = error?.message || 'Failed to remove member';
      captureError(error as Error, { feature: 'member-remove' });
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
  };

  const transferOwnership = async () => {
    if (!currentWorkspace || !transferTargetUserId) return;
    setIsTransferring(true);
    try {
      const { error } = await supabase.rpc('transfer_workspace_ownership', {
        p_workspace_id: currentWorkspace.id,
        p_new_owner_user_id: transferTargetUserId,
      });
      if (error) throw error;
      toast({ description: 'Workspace ownership transferred.' });
      setTransferDialogOpen(false);
      setTransferTargetUserId(null);

      // CRITICAL: WorkspaceContext caches currentWorkspace.role. After a
      // successful transfer the viewer is no longer 'owner' — they're now
      // 'admin'. Without invalidation, viewerIsOwner stays true until the
      // next route change / refresh and the UI keeps showing owner-only
      // controls (Make Owner, Admin invite option) that the API will reject.
      await refetchWorkspaces();
      await queryClient.invalidateQueries({ queryKey: queryKeys.workspaces.all });
      await fetchMembers();
    } catch (e: any) {
      captureError(e as Error, { feature: 'settings-member-settings-transfer-ownership' });
      toast({
        title: 'Transfer failed',
        description: e?.message ?? 'Unable to transfer ownership',
        variant: 'destructive',
      });
    } finally {
      setIsTransferring(false);
    }
  };

  const inviteMember = async () => {
    if (!currentWorkspace || !inviteEmail.trim()) return;

    setIsInviting(true);
    try {
      // First, create the invitation record
      const { data: invitation, error: dbError } = await supabase
        .from('workspace_invitations')
        .insert({
          workspace_id: currentWorkspace.id,
          invitee_email: inviteEmail,
          inviter_id: user?.id,
          member_role: inviteRole,
        })
        .select('id')
        .single();

      if (dbError) throw dbError;

      // Then send the invitation email
      try {
        const { sendInvitationEmail } = await import('../../lib/email/invitation-service');
        const baseUrl = window.location.origin;
        
        const emailResult = await sendInvitationEmail({
          invitationId: invitation.id,
          workspaceId: currentWorkspace.id,
          workspaceName: currentWorkspace.name,
          inviterName: user?.email || 'Someone',
          inviteeEmail: inviteEmail,
          role: inviteRole,
          baseUrl,
        });

        if (!emailResult.success) {
          console.warn('Failed to send invitation email:', emailResult.error);
          // Don't fail the invitation if email fails - just warn
        }
      } catch (emailError) {
        console.warn('Failed to send invitation email:', emailError);
        // Don't fail the invitation if email fails - just warn
      }

      toast({
        description: "Invitation sent successfully",
      });
      
      setInviteDialogOpen(false);
      setInviteEmail('');
      setInviteRole('member');
      await fetchPendingInvites();
    } catch (error) {
      captureError(error as Error, { feature: 'send-invitation' });
      toast({
        title: "Error",
        description: "Failed to send invitation",
        variant: "destructive",
      });
    } finally {
      setIsInviting(false);
    }
  };

  const resendInvite = async (inviteId: string) => {
    try {
      setIsInviting(true);
      
      // First, get the invitation details for resending email
      const { data: invitation, error: fetchError } = await supabase
        .from('workspace_invitations')
        .select('id, workspace_id, invitee_email, member_role')
        .eq('id', inviteId)
        .single();

      if (fetchError) throw fetchError;

      // Update the invitation timestamp
      const { error: updateError } = await supabase
        .from('workspace_invitations')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', inviteId);

      if (updateError) throw updateError;

      // Send the email again
      try {
        const { sendInvitationEmail } = await import('../../lib/email/invitation-service');
        const baseUrl = window.location.origin;
        
        const emailResult = await sendInvitationEmail({
          invitationId: invitation.id,
          workspaceId: invitation.workspace_id,
          workspaceName: currentWorkspace?.name || 'Workspace',
          inviterName: user?.email || 'Someone',
          inviteeEmail: invitation.invitee_email,
          role: invitation.member_role,
          baseUrl,
        });

        if (!emailResult.success) {
          console.warn('Failed to resend invitation email:', emailResult.error);
        }
      } catch (emailError) {
        console.warn('Failed to resend invitation email:', emailError);
      }

      toast({
        description: "Invitation resent successfully",
      });
      await fetchPendingInvites();
    } catch (error) {
      captureError(error as Error, { feature: 'settings-member-resend-invite' });
      toast({
        title: "Error",
        description: "Failed to resend invitation",
        variant: "destructive",
      });
    } finally {
      setIsInviting(false);
    }
  };

  const cancelInvite = async (inviteId: string) => {
    try {
      const { error } = await supabase
        .from('workspace_invitations') 
        .update({ status: 'cancelled' })
        .eq('id', inviteId);

      if (error) throw error;

      toast({
        description: "Invitation cancelled successfully",
      });
      await fetchPendingInvites();
    } catch (error) {
      captureError(error as Error, { feature: 'settings-member-settings-cancel-invite' });
      toast({
        title: "Error",
        description: "Failed to cancel invitation",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return <div className="py-8 text-center">Loading members...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Members</h2>
          <p className="text-muted-foreground">
            Manage workspace members and their permissions
          </p>
        </div>
        <Button onClick={() => setInviteDialogOpen(true)}>
          <UserPlus className="h-4 w-4 mr-2" />
          Invite Member
        </Button>
      </div>

      <div className="space-y-6">
        {/* Pending Invites Section */}
        {pendingInvites.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">Pending Invitations</h3>
              <Badge variant="secondary">{pendingInvites.length}</Badge>
            </div>
            <div className="space-y-2">
              {pendingInvites.map((invite) => (
                <div
                  key={invite.id}
                  className="flex items-center justify-between p-4 border rounded-lg bg-muted/50"
                >
                  <div className="space-y-1">
                    <div className="font-medium">{invite.invitee_email}</div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>Invited as {invite.member_role}</span>
                      <span>•</span>
                      <span>Sent {format(new Date(invite.created_at), 'MMM d, yyyy')}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => resendInvite(invite.id)}
                      disabled={isInviting}
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Resend
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => cancelInvite(invite.id)}
                      className="text-destructive"
                    >
                      <UserX className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Active Members */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">Active Members</h3>
            <Badge variant="secondary">{members.filter(m => !m.is_pending).length}</Badge>
          </div>
          {members.filter(m => !m.is_pending).map((member) => (
            <div
              key={member.user_id}
              className="flex items-center justify-between p-4 border rounded-lg"
            >
              <div className="flex items-center gap-4">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={member.avatar_url} />
                  <AvatarFallback>
                    {member.first_name?.[0]}{member.last_name?.[0]}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="font-medium">
                    {member.first_name} {member.last_name}
                    {member.role === 'owner' && (
                      <Badge variant="secondary" className="ml-2">Owner</Badge>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {member.email}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setSelectedMember(member);
                    setSharingDialogOpen(true);
                  }}
                  title="View shared contacts & groups"
                >
                  <Share2 className="h-4 w-4" />
                </Button>

                {viewerIsOwner && member.user_id !== user?.id && member.role !== 'owner' && member.status === 'active' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setTransferTargetUserId(member.user_id);
                      setTransferDialogOpen(true);
                    }}
                  >
                    Make Owner
                  </Button>
                )}

                <Select
                  value={member.role}
                  onValueChange={(value) => updateMemberRole(member.user_id, value)}
                  disabled={isSingleMember || member.role === 'owner'}
                >
                  <SelectTrigger className="w-[110px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="member">Member</SelectItem>
                    {/* 'owner' deliberately omitted — use Transfer Ownership */}
                  </SelectContent>
                </Select>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => updateMemberStatus(
                    member.user_id,
                    member.status === 'suspended' ? 'active' : 'suspended'
                  )}
                  title={member.status === 'suspended' ? 'Activate' : 'Suspend'}
                  className={member.status === 'suspended' ? 'text-orange-500' : ''}
                  disabled={isSingleMember || member.is_owner}
                >
                  <Ban className="h-4 w-4" />
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeMember(member.user_id)}
                  className="text-destructive"
                  title="Remove member"
                  disabled={isSingleMember || member.is_owner}
                >
                  <UserX className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <MemberSharingDialog
        open={sharingDialogOpen}
        onOpenChange={setSharingDialogOpen}
        member={selectedMember}
      />

      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Send an invitation to join your workspace
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Email</label>
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="colleague@company.com"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Role</label>
              <Select
                value={inviteRole}
                onValueChange={(value: 'admin' | 'member') => setInviteRole(value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {viewerIsOwner && <SelectItem value="admin">Admin</SelectItem>}
                  <SelectItem value="member">Member</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={inviteMember} disabled={isInviting}>
              {isInviting ? "Sending..." : "Send Invitation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transfer Workspace Ownership</DialogTitle>
            <DialogDescription>
              You will become an admin. The selected member will become the
              workspace owner. This change is immediate and cannot be undone
              without the new owner transferring back.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm">
            New owner:&nbsp;
            <strong>
              {(() => {
                const t = members.find(m => m.user_id === transferTargetUserId);
                return t ? `${t.first_name ?? ''} ${t.last_name ?? ''} (${t.email})` : '';
              })()}
            </strong>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setTransferDialogOpen(false)}
              disabled={isTransferring}
            >Cancel</Button>
            <Button onClick={transferOwnership} disabled={isTransferring}>
              {isTransferring ? 'Transferring...' : 'Confirm Transfer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}